export const BASE_URL = import.meta.env.VITE_API_URL || 'https://bookmygeo11-production.up.railway.app';
export const API_URL = `${BASE_URL}/api`;

export const getFullImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1540544660431-98411d9b7d99?auto=format&fit=crop&w=400&q=50&fm=webp';
  
  if (typeof url === 'object') {
    url = url.imageUrl || url.image || url.url || url.path || url.fileUrl || '';
  }
  
  if (typeof url !== 'string' || !url) return 'https://images.unsplash.com/photo-1540544660431-98411d9b7d99?auto=format&fit=crop&w=400&q=50&fm=webp';

  if (url.startsWith('http') || url.startsWith('data:')) {
    return url;
  }
  
  const cleanBase = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
  let path = url.startsWith('/') ? url : `/${url}`;
  
  if (!path.startsWith('/uploads/') && !path.startsWith('/api/')) {
    path = `/uploads${path}`;
  }
  
  return `${cleanBase}${path}`;
};
// NOTE: Do NOT log API_URL here — it exposes the backend address in the browser console.

export const getAuthHeaders = (isFormData = false) => {
  const path = window.location.pathname;

  const adminData = localStorage.getItem("adminLoginData");
  const hostData = localStorage.getItem("hostLoginData");
  const userData = localStorage.getItem("userLoginData");
  const employeeData = localStorage.getItem("employeeLoginData");

  const isJwtExpired = (t) => {
    if (!t || typeof t !== 'string') return false;
    try {
      const parts = t.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp ? Date.now() >= payload.exp * 1000 : false;
    } catch {
      return false;
    }
  };

  const parseOrNull = (data) => {
    if (!data) return null;
    try {
      const rawData = JSON.parse(data);
      const parsed = rawData.data || rawData;
      const token = parsed.token || rawData.token;
      if (token) {
        if (isJwtExpired(token)) return null;
        // Ensure id is also available at the top level of the returned object
        const id = parsed.userId || parsed.id || parsed.adminId || parsed.hostId || rawData.userId || rawData.adminId || rawData.hostId;
        return { ...parsed, token, id };
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const admin = parseOrNull(adminData);
  const host = parseOrNull(hostData);
  const user = parseOrNull(userData);
  const employee = parseOrNull(employeeData);

  let token = null;

  // Strict prioritization based on route
  if (path.includes('/admin') && admin) {
    token = admin.token || (admin.data && admin.data.token);
  } else if (path.includes('/host')) {
    if (host) {
      token = host.token || (host.data && host.data.token);
    } else if (employee) {
      token = employee.token || (employee.data && employee.data.token);
    }
  } else if (path.includes('/employee') && employee) {
    token = employee.token || (employee.data && employee.data.token);
  } else if (path.includes('/user') && user) {
    token = user.token || (user.data && user.data.token);
  } else {
    // Fallback order: Admin > Host > Employee > User
    if (admin) {
      token = admin.token || (admin.data && admin.data.token);
    } else if (host) {
      token = host.token || (host.data && host.data.token);
    } else if (employee) {
      token = employee.token || (employee.data && employee.data.token);
    } else if (user) {
      token = user.token || (user.data && user.data.token);
    }
  }

  const headers = {};
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Debounce guard: prevent multiple simultaneous 401s from firing multiple redirects
let _authRedirectPending = false;

export const apiFetch = async (url, options = {}) => {
  // Extract noRedirect before passing options to fetch
  const { noRedirect, ...fetchOptions } = options;

  const headers = getAuthHeaders();

  if (fetchOptions.headers) {
    Object.assign(headers, fetchOptions.headers);
  }

  if (fetchOptions.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const response = await fetch(url, { ...fetchOptions, headers });

  if (response.status === 401 || response.status === 403) {
    // If caller opts out of redirect (non-critical request), just return the response
    if (noRedirect) return response;

    const hasAuth = !!headers['Authorization'];
    const isExplicitAuthCheck = url.includes('/auth/me') || url.includes('/auth/verify') || url.includes('/auth/validate');
    
    if ((hasAuth || isExplicitAuthCheck) && !_authRedirectPending) {
      _authRedirectPending = true;
      setTimeout(() => {
        const path = window.location.pathname;
        if (path.includes('/admin')) {
          localStorage.removeItem('adminLoginData');
          window.location.href = '/admin-login';
        } else if (path.includes('/host')) {
          localStorage.removeItem('hostLoginData');
          window.location.href = '/host-login';
        } else if (path.includes('/employee')) {
          localStorage.removeItem('employeeLoginData');
          window.location.href = '/employee-login';
        } else if (path.includes('/user')) {
          localStorage.removeItem('userLoginData');
          window.location.href = '/user-login';
        }
        _authRedirectPending = false;
      }, 300);
    }
  }

  return response;
};

export const fetchProperties = async (department, category = null, feature = null) => {
  try {
    let url = `${API_URL}/properties/${department}`;
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (feature) params.append('feature', feature);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await apiFetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching properties:', error);
    throw error;
  }
};


export const fetchAllProperties = async () => {
  try {
    const response = await apiFetch(`${API_URL}/properties/all`);
    if (!response.ok) throw new Error('Failed to fetch all properties');
    return await response.json();
  } catch (error) {
    console.error('Error fetching all properties:', error);
    throw error;
  }
};

export const fetchAllPropertiesForUser = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/all/user/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch properties for user');
    return await response.json();
  } catch (error) {
    console.error('Error fetching properties for user:', error);
    throw error;
  }
};

export const fetchPopularProperties = async () => {
  try {
    const response = await apiFetch(`${API_URL}/properties/popular`);
    if (!response.ok) throw new Error('Failed to fetch popular properties');
    return await response.json();
  } catch (error) {
    console.error('Error fetching popular properties:', error);
    throw error;
  }
};

export const fetchPopularPropertiesForUser = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/popular/user/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch popular properties for user');
    return await response.json();
  } catch (error) {
    console.error('Error fetching popular properties for user:', error);
    throw error;
  }
};

export const fetchAdminProperties = async () => {
  try {
    const response = await apiFetch(`${API_URL}/properties/admin/all`);
    if (!response.ok) throw new Error('Failed to fetch admin properties');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin properties:', error);
    throw error;
  }
};

export const createProperty = async (department, propertyData) => {
  try {
    // Use the new generic properties endpoint
    const response = await apiFetch(`${API_URL}/properties/${department}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(propertyData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.details || 'Failed to create property');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating property:', error);
    throw error;
  }
};

export const createBooking = async (department, bookingData) => {
  const endpointMap = {
    'Hotel': '/bookings/hotel',
    'Salon': '/bookings/salon',
    'Hospital': '/bookings/hospital',
    'Cab': '/bookings/cab'
  };

  let endpoint = endpointMap[department];

  // If no specific endpoint, use the generic one
  if (!endpoint) {
    endpoint = `/bookings/${department}`;
  }

  try {
    const response = await apiFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('API Error details:', errorData);
      throw new Error(errorData.message || errorData.details || errorData.error || 'Failed to create booking');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

export const fetchBookings = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/users/${userId}`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

export const fetchHostBookings = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/hosts/${hostId}`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host bookings:', error);
    throw error;
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/register/user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Registration failed');
    }
    return await response.json();
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const registerHost = async (hostData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/register/host`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hostData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Registration failed');
    }
    return await response.json();
  } catch (error) {
    console.error('Error registering host:', error);
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/login/user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    if (!response.ok) throw new Error('Invalid credentials');
    return await response.json();
  } catch (error) {
    console.error('Error logging in user:', error);
    throw error;
  }
};

export const loginWithGoogle = async (googleData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/google/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(googleData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Google authentication failed');
    }
    return await response.json();
  } catch (error) {
    console.error('Error in Google login:', error);
    throw error;
  }
};

export const loginHost = async (credentials) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/login/host`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    if (!response.ok) throw new Error('Invalid credentials');
    return await response.json();
  } catch (error) {
    console.error('Error logging in host:', error);
    throw error;
  }
};

export const loginAdmin = async (credentials) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/login/admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    if (!response.ok) throw new Error('Invalid credentials');
    return await response.json();
  } catch (error) {
    console.error('Error logging in admin:', error);
    throw error;
  }
};

export const sendOtp = async (otpData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/otp/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(otpData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to send OTP');
    return data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

export const sendForgotPasswordOtp = async (otpData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/password/forgot/otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(otpData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to send OTP');
    return data;
  } catch (error) {
    console.error('Error sending forgot password OTP:', error);
    throw error;
  }
};

export const loginWithOtp = async (otpCredentials) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/otp/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(otpCredentials)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Invalid or expired OTP');
    return data;
  } catch (error) {
    console.error('Error logging in with OTP:', error);
    throw error;
  }
};

export const verifyOtp = async (verifyData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/otp/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(verifyData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Invalid or expired OTP');
    return data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

export const resetPassword = async (resetData) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/password/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(resetData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to reset password');
    return data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

// Fetch all hosts (for admin)
export const fetchHosts = async () => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts`);
    if (!response.ok) throw new Error('Failed to fetch hosts');
    return await response.json();
  } catch (error) {
    console.error('Error fetching hosts:', error);
    throw error;
  }
};

// Fetch single host by ID
export const fetchHostById = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch host');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching host:', error);
    throw error;
  }
};

// Fetch all properties for a specific host
export const fetchHostProperties = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch host properties');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host properties:', error);
    throw error;
  }
};

// Fetch all bookings (for admin)
export const fetchAllBookings = async () => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/admin`);
    if (!response.ok) throw new Error('Failed to fetch all bookings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching all bookings:', error);
    throw error;
  }
};

// Fetch all users (for admin)
export const fetchAllUsers = async () => {
  try {
    const response = await apiFetch(`${API_URL}/auth/users`);
    if (!response.ok) throw new Error('Failed to fetch users');
    return await response.json();
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Fetch dashboard statistics (for admin)
export const fetchDashboardStats = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/stats`);
    if (!response.ok) throw new Error('Failed to fetch statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching statistics:', error);
    throw error;
  }
};

// Fetch all users (Admin)
export const fetchUsers = async () => {
  try {
    const response = await apiFetch(`${API_URL}/auth/users`);
    if (!response.ok) throw new Error('Failed to fetch users');
    return await response.json();
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Update booking status
export const updateBookingStatus = async (department, bookingId, status, refundMethod = null, refundDetails = null, completionOtp = null) => {
  try {
    const body = { status };
    if (refundMethod) body.refundMethod = refundMethod;
    if (refundDetails) body.refundDetails = refundDetails;
    if (completionOtp) body.completionOtp = completionOtp;
    const response = await apiFetch(`${API_URL}/bookings/${department}/${bookingId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to update booking status');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating booking status:', error);
    throw error;
  }
};

// Request a refund for a booking (user-initiated)
export const requestBookingRefund = async (bookingId, action = 'request', notes = '') => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, notes })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to request refund');
    }
    return await response.json();
  } catch (error) {
    console.error('Error requesting refund:', error);
    throw error;
  }
};

// Delete booking
export const deleteBooking = async (department, bookingId) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${department}/${bookingId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete booking');
    return await response.json();
  } catch (error) {
    console.error('Error deleting booking:', error);
    throw error;
  }
};

// Update property status
export const updatePropertyStatus = async (department, propertyId, status, remarks) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}/action`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: status, remarks })
    });
    if (!response.ok) throw new Error('Failed to update property status');
    return await response.json();
  } catch (error) {
    console.error('Error updating property status:', error);
    throw error;
  }
};

// Delete property
export const deleteProperty = async (department, propertyId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete property');
    return await response.json();
  } catch (error) {
    console.error('Error deleting property:', error);
    throw error;
  }
};

// Update property
export const updateProperty = async (department, propertyId, propertyData) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(propertyData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to update property');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating property:', error);
    throw error;
  }
};


// Departments
// Departments
export const fetchDepartments = async (includeInactive = false) => {
  try {
    const query = includeInactive ? '?includeInactive=true' : '';
    const response = await apiFetch(`${API_URL}/departments${query}`);
    if (!response.ok) throw new Error('Failed to fetch departments');
    const json = await response.json();
    return Array.isArray(json) ? json : (json?.data || []);
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Category Divisions (Product Service / Product Manufacture)
export const fetchCategoryDivisions = async () => {
  try {
    const response = await apiFetch(`${API_URL}/category-divisions`);
    if (!response.ok) throw new Error('Failed to fetch category divisions');
    const json = await response.json();
    return Array.isArray(json) ? json : (json?.data || []);
  } catch (error) {
    console.error('Error fetching category divisions:', error);
    return [];
  }
};

export const fetchDepartmentDetails = async (name) => {
  try {
    const response = await apiFetch(`${API_URL}/departments/details/${name}`);
    if (!response.ok) throw new Error('Failed to fetch department details');
    return await response.json();
  } catch (error) {
    console.error('Error fetching department details:', error);
    throw error;
  }
};

export const createDepartment = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/departments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create department');
    return await response.json();
  } catch (error) {
    console.error('Error creating department:', error);
    throw error;
  }
};

export const updateDepartmentStatus = async (id, status) => {
  try {
    const response = await apiFetch(`${API_URL}/departments/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update department status');
    return await response.json();
  } catch (error) {
    console.error('Error updating department status:', error);
    throw error;
  }
};

export const updateDepartment = async (id, data) => {
  const res = await apiFetch(`${API_URL}/departments/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || json.error || 'Failed to update department');
  }
  return json;
};

export const deleteDepartment = (id) => apiFetch(`${API_URL}/departments/${id}`, {
  method: 'DELETE'
}).then(res => res.json());

export const fetchQuickServices = async () => {
  try {
    const response = await apiFetch(`${API_URL}/dynamic/quick-services`);
    if (!response.ok) throw new Error('Failed to fetch quick services');
    return await response.json();
  } catch (error) {
    console.error('Error fetching quick services:', error);
    throw error;
  }
};

export const createQuickService = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/dynamic/quick-services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create quick service');
    return await response.json();
  } catch (error) {
    console.error('Error creating quick service:', error);
    throw error;
  }
};

export const deleteQuickService = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/dynamic/quick-services/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete quick service');
    return await response.json();
  } catch (error) {
    console.error('Error deleting quick service:', error);
    throw error;
  }
};

// Offers
export const fetchOffers = async (userId = null) => {
  try {
    const query = userId ? `?userId=${userId}` : '';
    const response = await apiFetch(`${API_URL}/offers${query}`);
    if (!response.ok) throw new Error('Failed to fetch offers');
    return await response.json();
  } catch (error) {
    console.error('Error fetching offers:', error);
    throw error;
  }
};

export const fetchActiveOffers = async (userId = null) => {
  try {
    const query = userId ? `?userId=${userId}` : '';
    const response = await apiFetch(`${API_URL}/offers/active${query}`);
    if (!response.ok) throw new Error('Failed to fetch active offers');
    return await response.json();
  } catch (error) {
    console.error('Error fetching active offers:', error);
    throw error;
  }
};

export const createOffer = async (data) => {
  try {
    const isFormData = data instanceof FormData;
    const isUrlEncoded = data instanceof URLSearchParams;
    let headers = {};
    if (!isFormData && !isUrlEncoded) {
        headers = { 'Content-Type': 'application/json' };
    } else if (isUrlEncoded) {
        headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    }
    const response = await apiFetch(`${API_URL}/offers`, {
      method: 'POST',
      headers,
      body: isFormData || isUrlEncoded ? data : JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create offer');
    return await response.json();
  } catch (error) {
    console.error('Error creating offer:', error);
    throw error;
  }
};

export const updateOffer = async (id, data) => {
  try {
    const isFormData = data instanceof FormData;
    const isUrlEncoded = data instanceof URLSearchParams;
    let headers = {};
    if (!isFormData && !isUrlEncoded) {
        headers = { 'Content-Type': 'application/json' };
    } else if (isUrlEncoded) {
        headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    }
    const response = await apiFetch(`${API_URL}/offers/${id}`, {
      method: 'PUT',
      headers,
      body: isFormData || isUrlEncoded ? data : JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update offer');
    return await response.json();
  } catch (error) {
    console.error('Error updating offer:', error);
    throw error;
  }
};

export const deleteOffer = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/offers/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete offer');
    return await response.json();
  } catch (error) {
    console.error('Error deleting offer:', error);
    throw error;
  }
};

// Notifications
export const fetchNotifications = async (userId, type = 'USER') => {
  try {
    const response = await apiFetch(`${API_URL}/notifications/${userId}?type=${type}`);
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return await response.json();
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const markNotificationRead = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/notifications/${id}/read`, {
      method: 'PUT'
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return await response.json();
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const clearNotifications = async (userId, type = 'USER') => {
  try {
    const response = await apiFetch(`${API_URL}/notifications/${userId}/clear?type=${type}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to clear notifications');
    return await response.json();
  } catch (error) {
    console.error('Error clearing notifications:', error);
    throw error;
  }
};

export const sendBulkNotification = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/notifications/send`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send notification');
    }
    return await response.json();
  } catch (error) {
    console.error('Error sending bulk notification:', error);
    throw error;
  }
};

export const registerDeviceToken = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/notifications/register-token`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to register device token');
    }
    return await response.json();
  } catch (error) {
    console.error('Error registering device token:', error);
    throw error;
  }
};

// Enhanced User/Host
export const requestUserAccountDeletion = async (id, reason) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/user/${id}/delete-request`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to submit deletion request');
    }
    return await response.json();
  } catch (error) {
    console.error('Error requesting user deletion:', error);
    throw error;
  }
};

export const requestHostAccountDeletion = async (id, reason) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts/${id}/delete-request`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to submit deletion request');
    }
    return await response.json();
  } catch (error) {
    console.error('Error requesting host deletion:', error);
    throw error;
  }
};

export const fetchDeletionRequests = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/deletion-requests`);
    if (!response.ok) throw new Error('Failed to fetch deletion requests');
    return await response.json();
  } catch (error) {
    console.error('Error fetching deletion requests:', error);
    throw error;
  }
};

export const approveDeletionRequest = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/deletion-requests/${id}/approve`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to approve deletion request');
    return await response.json();
  } catch (error) {
    console.error('Error approving deletion request:', error);
    throw error;
  }
};

export const rejectDeletionRequest = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/deletion-requests/${id}/reject`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to reject deletion request');
    return await response.json();
  } catch (error) {
    console.error('Error rejecting deletion request:', error);
    throw error;
  }
};

export const updateUserProfile = async (id, data) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/users/${id}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update user profile');
    return await response.json();
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const updateUserDepartments = async (userId, departmentIds) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/user/${userId}/departments`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(departmentIds)
    });
    if (!response.ok) throw new Error('Failed to update user departments');
    return await response.json();
  } catch (error) {
    console.error('Error updating user departments:', error);
    throw error;
  }
};

export const updateHostProfile = async (id, data) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts/${id}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update host profile');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating host profile:', error);
    throw error;
  }
};

export const updateHostStatus = async (id, action) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action })
    });
    if (!response.ok) throw new Error('Failed to update host status');
    return await response.json();
  } catch (error) {
    console.error('Error updating host status:', error);
    throw error;
  }
};

// Wallet & Points
export const fetchWalletTransactions = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/wallet/transactions/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch wallet transactions');
    return await response.json();
  } catch (error) {
    console.error('Error fetching wallet transactions:', error);
    throw error;
  }
};

export const fetchPointsHistory = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/wallet/points/history/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch points history');
    return await response.json();
  } catch (error) {
    console.error('Error fetching points history:', error);
    throw error;
  }
};

export const convertPoints = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/wallet/points/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Conversion failed');
    return data;
  } catch (error) {
    console.error('Error converting points:', error);
    throw error;
  }
};

export const fetchConversionRate = async () => {
  try {
    const response = await apiFetch(`${API_URL}/wallet/points/rate`);
    if (!response.ok) throw new Error('Failed to fetch conversion rate');
    return await response.json();
  } catch (error) {
    console.error('Error fetching conversion rate:', error);
    throw error;
  }
};

// ---------- Coupons API ----------
export const fetchCoupons = async (userId = null) => {
  try {
    const query = userId ? `?userId=${userId}` : '';
    const response = await apiFetch(`${API_URL}/coupons${query}`);
    if (!response.ok) throw new Error('Failed to fetch coupons');
    return await response.json();
  } catch (error) {
    console.error('Error fetching coupons:', error);
    throw error;
  }
};

export const createCoupon = async (couponData) => {
  try {
    const response = await apiFetch(`${API_URL}/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(couponData)
    });
    if (!response.ok) throw new Error('Failed to create coupon');
    return await response.json();
  } catch (error) {
    console.error('Error creating coupon:', error);
    throw error;
  }
};

export const createHostCoupon = async (couponData) => {
  try {
    const response = await apiFetch(`${API_URL}/host/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(couponData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create host coupon');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating host coupon:', error);
    throw error;
  }
};

export const updateCoupon = async (id, data) => {
  try {
    const response = await apiFetch(`${API_URL}/coupons/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update coupon');
    return await response.json();
  } catch (error) {
    console.error('Error updating coupon:', error);
    throw error;
  }
};

export const deleteCoupon = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/coupons/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete coupon');
    return await response.json();
  } catch (error) {
    console.error('Error deleting coupon:', error);
    throw error;
  }
};

export const validateCoupon = async (code, userId = null, propertyId = null) => {
  try {
    const body = { code };
    if (userId) body.userId = userId;
    if (propertyId) body.propertyId = propertyId;

    const response = await apiFetch(`${API_URL}/coupons/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok) {
      return { valid: false, message: data.error || 'Validation failed' };
    }

    return data;
  } catch (error) {
    console.error('Error validating coupon:', error);
    throw error;
  }
};

export const applyBookingCoupon = async (bookingId, couponData) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/coupon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(couponData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to apply coupon');
    return data;
  } catch (error) {
    console.error('Error applying coupon:', error);
    throw error;
  }
};

// ---------- Subscriptions API ----------
export const fetchSubscriptions = async () => {
  try {
    const response = await apiFetch(`${API_URL}/subscriptions`);
    if (!response.ok) throw new Error('Failed to fetch subscriptions');
    return await response.json();
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    throw error;
  }
};

export const fetchHostSubscription = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/host/${hostId}/subscription`, { noRedirect: true });
    if (!response.ok) throw new Error('Failed to fetch host subscription');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host subscription:', error);
    throw error;
  }
};

export const fetchHostAccessStatus = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/host/${hostId}/access-status`);
    if (!response.ok) throw new Error('Failed to fetch host access status');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host access status:', error);
    throw error;
  }
};

export const createSubscription = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/subscriptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create subscription');
    return await response.json();
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

export const updateSubscription = async (id, data) => {
  try {
    const response = await apiFetch(`${API_URL}/subscriptions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update subscription');
    return await response.json();
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

export const deleteSubscription = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/subscriptions/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete subscription');
    return await response.json();
  } catch (error) {
    console.error('Error deleting subscription:', error);
    throw error;
  }
};

export const updateSubscriptionStatus = async (id, status) => {
  try {
    const response = await apiFetch(`${API_URL}/subscriptions/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update subscription status');
    return await response.json();
  } catch (error) {
    console.error('Error updating subscription status:', error);
    throw error;
  }
};

// ---------- Membership Templates API ----------
export const fetchMembershipTemplates = async () => {
  try {
    const response = await apiFetch(`${API_URL}/membership/templates`);
    if (!response.ok) throw new Error('Failed to fetch membership templates');
    return await response.json();
  } catch (error) {
    console.error('Error fetching membership templates:', error);
    throw error;
  }
};

export const fetchHostMembershipTemplates = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/host/${hostId}/membership-templates`);
    if (!response.ok) throw new Error('Failed to fetch host membership templates');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host membership templates:', error);
    throw error;
  }
};

export const fetchAllMembershipCardDesigns = async () => {
  try {
    const response = await apiFetch(`${API_URL}/membership/cards/designs/all`);
    if (!response.ok) throw new Error('Failed to fetch membership card designs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching membership card designs:', error);
    throw error;
  }
};

export const deleteMembershipCardDesign = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/cards/designs/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete membership card design');
    return await response.json();
  } catch (error) {
    console.error('Error deleting membership card design:', error);
    throw error;
  }
};
export const fetchAllCardDesigns = async () => {
  try {
    const response = await apiFetch(`${API_URL}/membership/cards/designs/all`);
    if (!response.ok) throw new Error('Failed to fetch all card designs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching all card designs:', error);
    throw error;
  }
};

export const fetchAdminCardDesigns = async () => {
  try {
    const response = await apiFetch(`${API_URL}/membership/admin/cards/designs`);
    if (!response.ok) throw new Error('Failed to fetch admin card designs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin card designs:', error);
    throw error;
  }
};

export const createAdminCardDesign = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/admin/cards/designs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create admin card design');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating admin card design:', error);
    throw error;
  }
};

export const fetchAllMembershipRequests = async () => {
  try {
    const response = await apiFetch(`${API_URL}/membership/all-requests`);
    if (!response.ok) throw new Error('Failed to fetch all membership requests');
    return await response.json();
  } catch (error) {
    console.error('Error fetching all membership requests:', error);
    throw error;
  }
};

export const updateAdminMembershipRequestStatus = async (requestId, status) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/host/requests/${requestId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update membership request status');
    return await response.json();
  } catch (error) {
    console.error('Error updating membership request status:', error);
    throw error;
  }
};

// ---------- Wallet & Rewards API ----------
export const fetchHostProfile = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/hosts/${id}`);
    if (!response.ok) throw new Error('Failed to fetch host profile');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching host profile:', error);
    throw error;
  }
};

export const fetchUserProfile = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/auth/user/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch user profile');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

// Wallet & Points
// (Already defined above)

// Points conversion logic
// (Already defined above)

export const createMembershipTemplate = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/host/membership-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create membership template');
    return await response.json();
  } catch (error) {



    throw error;
  }
};

// ---------- System Settings API ----------
export const fetchSystemSettings = async (prefix = "") => {
  try {
    const url = prefix ? `${API_URL}/system-settings/${prefix}` : `${API_URL}/system-settings`;
    const response = await apiFetch(url);
    if (!response.ok) throw new Error('Failed to fetch system settings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching system settings:', error);
    throw error;
  }
};

export const fetchSystemSettingByKey = async (key) => {
  try {
    const settings = await fetchSystemSettings();
    const setting = settings.find(s => s.settingKey === key);
    return setting ? setting.settingValue : null;
  } catch (error) {
    console.error(`Error fetching system setting for key ${key}:`, error);
    throw error;
  }
};

export const saveSystemSettings = async (settings) => {
  try {
    const response = await apiFetch(`${API_URL}/system-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to save system settings');
    return await response.json();
  } catch (error) {
    console.error('Error saving system settings:', error);
    throw error;
  }
};



export const updateMembershipTemplate = async (id, data) => {
  try {
    const response = await apiFetch(`${API_URL}/host/membership-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update membership template');
    return await response.json();
  } catch (error) {
    console.error('Error updating membership template:', error);
    throw error;
  }
};

export const deleteMembershipTemplate = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/host/membership-templates/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete membership template');
    return await response.json();
  } catch (error) {
    console.error('Error deleting membership template:', error);
    throw error;
  }
};

// ---------- Support Queries API ----------
export const fetchUserQueries = async () => {
  try {
    const response = await apiFetch(`${API_URL}/support/user-queries`);
    if (!response.ok) throw new Error('Failed to fetch user queries');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user queries:', error);
    throw error;
  }
};

export const fetchHostQueries = async () => {
  try {
    const response = await apiFetch(`${API_URL}/support/host-queries`);
    if (!response.ok) throw new Error('Failed to fetch host queries');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host queries:', error);
    throw error;
  }
};

export const replyToQuery = async (id, adminId, reply) => {
  try {
    const response = await apiFetch(`${API_URL}/support/queries/${id}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminId, reply })
    });
    if (!response.ok) throw new Error('Failed to reply to query');
    return await response.json();
  } catch (error) {
    console.error('Error replying to query:', error);
    throw error;
  }
};

export const fetchTicketMessages = async (ticketId) => {
  try {
    const response = await apiFetch(`${API_URL}/support/queries/${ticketId}/messages`);
    if (!response.ok) throw new Error('Failed to fetch ticket messages');
    return await response.json();
  } catch (error) {
    console.error('Error fetching ticket messages:', error);
    throw error;
  }
};

export const sendSupportMessage = async (ticketId, messageData) => {
  try {
    const response = await apiFetch(`${API_URL}/support/queries/${ticketId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(messageData)
    });
    if (!response.ok) throw new Error('Failed to send message');
    return await response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const submitSupportQuery = async (queryData) => {
  try {
    const response = await apiFetch(`${API_URL}/support/queries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(queryData)
    });

    // Try to parse JSON response. If successful, use its error message.
    // If parsing fails (e.g. 404 HTML), throw generic error.
    let data;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    }

    if (!response.ok) {
      const errorMessage = data && (data.message || data.error)
        ? (data.message || data.error)
        : `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    return data; // Return successfully parsed data
  } catch (error) {
    console.error('Error submitting query:', error);
    throw error; // Re-throw to be caught by component
  }
};

export const fetchUserTickets = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/support/user/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch user tickets');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user tickets:', error);
    throw error;
  }
};

export const fetchHostTickets = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/support/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch host tickets');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host tickets:', error);
    throw error;
  }
};

// ---------- Membership Cards API ----------
export const fetchHostDesigns = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/hosts/${hostId}/membership-cards/designs`);
    if (!response.ok) throw new Error('Failed to fetch host designs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host designs:', error);
    throw error;
  }
};


export const createMembershipRequest = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/cards/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create membership request');
    return await response.json();
  } catch (error) {
    console.error('Error creating membership request:', error);
    throw error;
  }
};

export const fetchUserMembershipRequests = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/user/${userId}/requests`);
    if (!response.ok) throw new Error('Failed to fetch user membership requests');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user membership requests:', error);
    throw error;
  }
};

export const fetchUserMembershipCards = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/user/${userId}/cards`);
    if (!response.ok) throw new Error('Failed to fetch user membership cards');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user membership cards:', error);
    throw error;
  }
};

export const fetchHostMembershipRequests = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/host/${hostId}/requests`);
    if (!response.ok) throw new Error('Failed to fetch host membership requests');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host membership requests:', error);
    throw error;
  }
};

export const fetchHostCardDesigns = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/host/${hostId}/cards/designs`);
    if (!response.ok) throw new Error('Failed to fetch host card designs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host card designs:', error);
    throw error;
  }
};

export const submitMembershipPayment = async (requestId, paymentData) => {
  try {
    const response = await apiFetch(`${API_URL}/membership/cards/request/${requestId}/payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to submit membership payment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error submitting membership payment:', error);
    throw error;
  }
};

// ---------- Subscription Payment Requests API ----------
export const fetchPendingSubscriptionPayments = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/subscription-payments/pending`);
    if (!response.ok) throw new Error('Failed to fetch pending subscription payments');
    return await response.json();
  } catch (error) {
    console.error('Error fetching pending subscription payments:', error);
    throw error;
  }
};

export const fetchAllSubscriptionPayments = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/subscription-payments`);
    if (!response.ok) throw new Error('Failed to fetch subscription payments');
    return await response.json();
  } catch (error) {
    console.error('Error fetching subscription payments:', error);
    throw error;
  }
};

export const approveSubscriptionPayment = async (paymentId, adminId) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/subscription-payments/${paymentId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminId })
    });
    if (!response.ok) throw new Error('Failed to approve subscription payment');
    return await response.json();
  } catch (error) {
    console.error('Error approving subscription payment:', error);
    throw error;
  }
};

export const rejectSubscriptionPayment = async (paymentId, adminId) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/subscription-payments/${paymentId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminId })
    });
    if (!response.ok) throw new Error('Failed to reject subscription payment');
    return await response.json();
  } catch (error) {
    console.error('Error rejecting subscription payment:', error);
  }
};

// ---------- Reviews API ----------
export const createReview = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    // Check if response has content
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error(`Server error: Expected JSON response but got ${contentType}. The backend may not have loaded the Review controller. Please restart the backend server.`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating review:', error);
    throw error;
  }
};

export const fetchBookingReview = async (bookingId) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/booking/${bookingId}`);
    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, message: 'No review found' };
      }
      throw new Error('Failed to fetch review');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching booking review:', error);
    throw error;
  }
};

export const updateReview = async (reviewId, data) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/${reviewId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update review');
    return await response.json();
  } catch (error) {
    console.error('Error updating review:', error);
    throw error;
  }
};

export const createReviewReply = async (reviewId, replyData) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/${reviewId}/replies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(replyData)
    });
    if (!response.ok) throw new Error('Failed to create review reply');
    return await response.json();
  } catch (error) {
    console.error('Error creating review reply:', error);
    throw error;
  }
};

export const fetchReviewReplies = async (reviewId) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/${reviewId}/replies`);
    if (!response.ok) throw new Error('Failed to fetch review replies');
    return await response.json();
  } catch (error) {
    console.error('Error fetching review replies:', error);
    throw error;
  }
};

export const fetchHostReviews = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch host reviews');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host reviews:', error);
    throw error;
  }
};

export const fetchApprovedReviews = async () => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/approved`);
    if (!response.ok) throw new Error('Failed to fetch approved reviews');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching approved reviews:', error);
    throw error;
  }
};

export const resolveMapUrl = async (url) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/resolve-url?url=${encodeURIComponent(url)}`);
    if (!response.ok) throw new Error('Failed to resolve URL');
    return await response.json();
  } catch (error) {
    console.error('Error resolving map URL:', error);
    throw error;
  }
};

// ---------- Property Images API ----------
export const fetchPropertyImages = async (department, propertyId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}/images`);
    if (!response.ok) throw new Error('Failed to fetch property images');
    return await response.json();
  } catch (error) {
    console.error('Error fetching property images:', error);
    throw error;
  }
};

export const addPropertyImages = async (department, propertyId, images) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ images })
    });
    if (!response.ok) throw new Error('Failed to add property images');
    return await response.json();
  } catch (error) {
    console.error('Error adding property images:', error);
    throw error;
  }
};

export const deletePropertyImage = async (department, propertyId, imageId) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/${department}/${propertyId}/images/${imageId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete property image');
    return await response.json();
  } catch (error) {
    console.error('Error deleting property image:', error);
    throw error;
  }
};


// Upload file (for receipts/images)
export const uploadFile = async (formData) => {
  try {
    const response = await apiFetch(`${API_URL}/upload/images`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) throw new Error('Failed to upload file');
    return await response.json();
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};


// ================================================
// BOOKING PAYMENT API FUNCTIONS
// ================================================

// Submit booking payment
export const submitBookingPayment = async (bookingId, paymentData) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to submit payment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error submitting payment:', error);
    throw error;
  }
};

// Get booking payment details — returns null if no record found (Cash on Arrival, etc.)
export const getBookingPayment = async (bookingId) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/payment`);
    const data = await response.json();
    if (response.ok) {
      return data; // Full payment record
    }
    // 404 — backend returns { paymentFound: false, bookingPaymentMethod, bookingStatus }
    // Return the body so the modal can inspect it
    if (response.status === 404) {
      return { paymentFound: false, ...data };
    }
    throw new Error(data.message || data.error || 'Failed to get payment details');
  } catch (error) {
    console.error('Error getting payment:', error);
    return null; // Fail gracefully
  }
};


// Verify booking payment (host)
export const verifyBookingPayment = async (bookingId, verificationData) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/payment/verify`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(verificationData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to verify payment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

// Reject booking payment (host)
export const rejectBookingPayment = async (bookingId, rejectionData) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/payment/reject`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rejectionData)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to reject payment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error rejecting payment:', error);
    throw error;
  }
};

// ---------- Specialists API ----------
export const fetchSpecialistAvailability = async (specialistId, date) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/specialist/${specialistId}/availability?date=${date}`);
    if (!response.ok) throw new Error('Failed to fetch availability');
    return await response.json();
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};

export const fetchSpecialistsByProperty = async (propertyId) => {
  try {
    const response = await apiFetch(`${API_URL}/specialists/property/${propertyId}`);
    if (!response.ok) throw new Error('Failed to fetch specialists');
    return await response.json();
  } catch (error) {
    console.error('Error fetching specialists:', error);
    throw error;
  }
};

export const addSpecialist = async (specialistData) => {
  try {
    const response = await apiFetch(`${API_URL}/specialists/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(specialistData)
    });
    if (!response.ok) throw new Error('Failed to add specialist');
    return await response.json();
  } catch (error) {
    console.error('Error adding specialist:', error);
    throw error;
  }
};

export const batchAddSpecialists = async (propertyId, specialists) => {
  try {
    const response = await apiFetch(`${API_URL}/specialists/batch-add/${propertyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(specialists)
    });
    if (!response.ok) throw new Error('Failed to add specialists in batch');
    return await response.json();
  } catch (error) {
    console.error('Error batch adding specialists:', error);
    throw error;
  }
};

export const deleteSpecialist = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/specialists/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete specialist');
    return await response.json();
  } catch (error) {
    console.error('Error deleting specialist:', error);
    throw error;
  }
};


// ---------- Offered Services API ----------
export const fetchServicesByProperty = async (propertyId) => {
  try {
    const response = await apiFetch(`${API_URL}/offered-services/property/${propertyId}`);
    if (!response.ok) throw new Error('Failed to fetch services');
    return await response.json();
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

export const addOfferedService = async (serviceData) => {
  try {
    const response = await apiFetch(`${API_URL}/offered-services/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(serviceData)
    });
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to add service (${response.status}): ${errorBody}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error adding service:', error);
    throw error;
  }
};

export const deleteOfferedService = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/offered-services/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete service');
    return await response.json();
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

export const fetchAdminBookings = async () => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/admin`);
    if (!response.ok) throw new Error('Failed to fetch admin bookings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin bookings:', error);
    throw error;
  }
};

export const processRefund = async (bookingId, action, notes) => {
  try {
    const response = await apiFetch(`${API_URL}/bookings/${bookingId}/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, notes })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || err.error || 'Failed to process refund');
    }
    return await response.json();
  } catch (error) {
    console.error('Error processing refund:', error);
    throw error;
  }
};
export const fetchServiceTimeAvailability = async (propertyId, startTime, endTime) => {
  try {
    const response = await apiFetch(`${API_URL}/offered-services/property/${propertyId}/availability/time?startTime=${startTime}&endTime=${endTime}`);
    if (!response.ok) throw new Error('Failed to fetch service time availability');
    return await response.json();
  } catch (error) {
    console.error('Error fetching service time availability:', error);
    throw error;
  }
};
// POS Endpoints
export const fetchPOSProducts = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/products/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch POS products');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS products:', error);
    throw error;
  }
};

export const createPOSProduct = async (hostId, product) => {
  try {
    // Ensure numeric fields are properly typed
    const payload = {
      ...product,
      price: product.price ? Number(product.price) : 0,
      stockQuantity: product.stockQuantity ? Number(product.stockQuantity) : 0,
      lowStockThreshold: product.lowStockThreshold ? Number(product.lowStockThreshold) : 5,
      gstPercentage: product.gstPercentage ? Number(product.gstPercentage) : 0,
    };

    const response = await apiFetch(`${API_URL}/pos/products/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to create POS product');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating POS product:', error);
    throw error;
  }
};

export const updatePOSProduct = async (productId, product) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/products/update/${productId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product)
    });
    if (!response.ok) throw new Error('Failed to update POS product');
    return await response.json();
  } catch (error) {
    console.error('Error updating POS product:', error);
    throw error;
  }
};

export const notifyVendorProductOutOfStock = async (productId, employeeName) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/products/${productId}/notify-vendor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeName })
    });
    if (!response.ok) throw new Error('Failed to notify vendor');
    return await response.json();
  } catch (error) {
    console.error('Error notifying vendor:', error);
    throw error;
  }
};

export const deletePOSProduct = async (productId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/products/${productId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete product: ${errorText}`);
    }
    return true;
  } catch (error) {
    console.error('Error deleting POS product:', error);
    throw error;
  }
};

export const fetchPOSInvoices = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/invoices/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch POS invoices');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS invoices:', error);
    throw error;
  }
};

export const createPOSInvoice = async (hostId, invoiceData) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/invoices/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoiceData)
    });
    if (!response.ok) throw new Error('Failed to create POS invoice');
    return await response.json();
  } catch (error) {
    console.error('Error creating POS invoice:', error);
    throw error;
  }
};

export const searchPOSCustomers = async (hostId, query = '') => {
  try {
    const response = await apiFetch(`${API_URL}/pos/customers/search/${hostId}?query=${query}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to search POS customers: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error searching POS customers:', error);
    throw error;
  }
};

export const createPOSCustomer = async (hostId, customerData) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/customers/${hostId}`, {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create POS customer: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating POS customer:', error);
    throw error;
  }
};

// POS Inventory APIs
export const fetchPOSSuppliers = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/suppliers/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch suppliers');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS suppliers:', error);
    throw error;
  }
};

export const createPOSSupplier = async (hostId, supplier) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/suppliers/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplier)
    });
    if (!response.ok) throw new Error('Failed to create supplier');
    return await response.json();
  } catch (error) {
    console.error('Error creating POS supplier:', error);
    throw error;
  }
};

export const adjustPOSStock = async (productId, adjustmentData) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/adjust/${productId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(adjustmentData)
    });
    if (!response.ok) throw new Error('Failed to adjust stock');
    return await response.json();
  } catch (error) {
    console.error('Error adjusting POS stock:', error);
    throw error;
  }
};

export const fetchPOSInventoryLogs = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/logs/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch inventory logs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS inventory logs:', error);
    throw error;
  }
};

export const fetchPOSLowStock = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/low-stock/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch low stock alert');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS low stock:', error);
    throw error;
  }
};

export const bulkUploadPOSProducts = async (hostId, products) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/bulk-upload/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(products)
    });
    if (!response.ok) throw new Error('Failed to bulk upload products');
    return await response.json();
  } catch (error) {
    console.error('Error bulk uploading POS products:', error);
    throw error;
  }
};
export const fetchPOSCategories = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/categories/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch POS categories');
    return await response.json();
  } catch (error) {
    console.error('Error fetching POS categories:', error);
    throw error;
  }
};

export const createPOSCategory = async (hostId, category) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/categories/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(category)
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to create POS category (${response.status}): ${text}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating POS category:', error);
    throw error;
  }
};

export const updatePOSCategory = async (hostId, categoryId, category) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/categories/${categoryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(category)
    });
    if (!response.ok) throw new Error('Failed to update category');
    return await response.json();
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deletePOSCategory = async (hostId, categoryId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/categories/${categoryId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete category');
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

export const updatePOSSupplier = async (hostId, supplierId, supplier) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/suppliers/${supplierId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplier)
    });
    if (!response.ok) throw new Error('Failed to update supplier');
    return await response.json();
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

export const deletePOSSupplier = async (hostId, supplierId) => {
  try {
    const response = await apiFetch(`${API_URL}/pos/inventory/suppliers/${supplierId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete supplier');
    return true;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

export const createPaymentOrder = async (amount, currency = 'INR', receipt = 'receipt#1', entityType = null, entityId = null) => {
  const response = await apiFetch(`${API_URL}/payment/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, currency, receipt, entityType, entityId }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to create Razorpay order');
  }
  return data;
};

export const verifyPayment = async (paymentData) => {
  const response = await apiFetch(`${API_URL}/payment/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(paymentData),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Payment verification failed');
  }
  return data;
};

export const fetchPaymentConfig = async () => {
  const response = await apiFetch(`${API_URL}/payment/config`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to fetch payment config');
  }
  return data;
};

// Microsoft Teams Meetings
export const fetchUserMeetings = async (userId) => {
  try {
    const response = await apiFetch(`${API_URL}/meetings/user/${userId}`);
    if (!response.ok) throw new Error('Failed to fetch user meetings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user meetings:', error);
    throw error;
  }
};

export const fetchHostMeetings = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/meetings/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch host meetings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host meetings:', error);
    throw error;
  }
};

export const updateMeetingRequest = async (meetingId, data) => {
  try {
    const response = await apiFetch(`${API_URL}/meetings/${meetingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update meeting request');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating meeting request:', error);
    throw error;
  }
};

// Company Chat APIs
export const fetchChatGroupHistory = async (companyId) => {
  try {
    const response = await apiFetch(`${API_URL}/chat/group/${companyId}`);
    if (!response.ok) throw new Error('Failed to fetch group chat history');
    return await response.json();
  } catch (error) {
    console.error('Error fetching group chat history:', error);
    throw error;
  }
};

export const fetchChatDMHistory = async (companyId, user1Id, user1Type, user2Id, user2Type) => {
  try {
    const response = await apiFetch(`${API_URL}/chat/dm?companyId=${companyId}&user1Id=${user1Id}&user1Type=${user1Type}&user2Id=${user2Id}&user2Type=${user2Type}`);
    if (!response.ok) throw new Error('Failed to fetch DM history');
    return await response.json();
  } catch (error) {
    console.error('Error fetching DM history:', error);
    throw error;
  }
};

export const fetchHostEmployees = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/employees/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch employees');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host employees:', error);
    throw error;
  }
};

// Vendor Items
export const fetchVendorItems = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/vendor/items/host/${hostId}`);
    if (!response.ok) throw new Error('Failed to fetch vendor items');
    return await response.json();
  } catch (error) {
    console.error('Error fetching vendor items:', error);
    throw error;
  }
};

export const createVendorItem = async (hostId, itemData) => {
  try {
    const response = await apiFetch(`${API_URL}/vendor/items/host/${hostId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(itemData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create vendor item');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating vendor item:', error);
    throw error;
  }
};

export const updateVendorItem = async (itemId, hostId, itemData) => {
  try {
    const response = await apiFetch(`${API_URL}/vendor/items/${itemId}/host/${hostId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(itemData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update vendor item');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating vendor item:', error);
    throw error;
  }
};

export const deleteVendorItem = async (itemId, hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/vendor/items/${itemId}/host/${hostId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete vendor item');
    return response;
  } catch (error) {
    console.error('Error deleting vendor item:', error);
    throw error;
  }
};

// Admin Vendor Items
export const fetchAllVendorItemsAdmin = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/items`);
    if (!response.ok) throw new Error('Failed to fetch admin vendor items');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin vendor items:', error);
    throw error;
  }
};

export const updateVendorItemStatusAdmin = async (id, statusData) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/items/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(statusData)
    });
    if (!response.ok) throw new Error('Failed to update item status');
    return await response.json();
  } catch (error) {
    console.error('Error updating item status:', error);
    throw error;
  }
};

export const deleteVendorItemAdmin = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/items/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete item');
    return response;
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
};

// Public Vendor Items
export const fetchPublicVendorItems = async (category = '', search = '') => {
  try {
    const params = new URLSearchParams();
    if (category && category !== 'All') params.append('category', category);
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiFetch(`${API_URL}/public/items${query}`);
    if (!response.ok) throw new Error('Failed to fetch public items');
    return await response.json();
  } catch (error) {
    console.error('Error fetching public items:', error);
    throw error;
  }
};

export const fetchPublicVendorItemDetails = async (id) => {
  try {
    const response = await apiFetch(`${API_URL}/public/items/${id}`);
    if (!response.ok) throw new Error('Failed to fetch item details');
    return await response.json();
  } catch (error) {
    console.error('Error fetching item details:', error);
    throw error;
  }
};

// --- Review Functions ---

export const fetchPropertyReviews = async (propertyId) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/property/${propertyId}`);
    if (!response.ok) throw new Error('Failed to fetch property reviews');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

// Admin Review Moderation
export const fetchAllReviewsAdmin = async () => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/admin/all`);
    if (!response.ok) throw new Error('Failed to fetch reviews for admin');
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

export const updateReviewStatusAdmin = async (id, status) => {
  try {
    const response = await apiFetch(`${API_URL}/reviews/${id}/status?status=${status}`, {
      method: "PUT"
    });
    if (!response.ok) throw new Error('Failed to update review status');
    return await response.json();
  } catch (error) {
    console.error('Error updating review status:', error);
    throw error;
  }
};

export const deleteReviewAdmin = async (id) => {
    try {
        const response = await apiFetch(`${API_URL}/reviews/${id}`, {
            method: "DELETE"
        });
        if (!response.ok) throw new Error('Failed to delete review');
        return await response.json();
    } catch (error) {
        console.error('Error deleting review:', error);
        throw error;
    }
};
// ---------- Host Withdrawal/Transaction API ----------
export const fetchWithdrawals = async (status = '') => {
  try {
    const query = status ? `?status=${status}` : '';
    const response = await apiFetch(`${API_URL}/transactions/admin/withdrawals${query}`);
    if (!response.ok) throw new Error('Failed to fetch withdrawals');
    return await response.json();
  } catch (error) {
    console.error('Error fetching withdrawals:', error);
    throw error;
  }
};

export const fetchAdminWallet = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/wallet`);
    if (!response.ok) throw new Error('Failed to fetch admin wallet');
    return await response.json();
  } catch (error) {
    console.error('Error fetching admin wallet:', error);
    throw error;
  }
};

export const approveWithdrawal = async (id, remarks = '') => {
  try {
    const response = await apiFetch(`${API_URL}/transactions/admin/withdrawals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ remarks })
    });
    if (!response.ok) throw new Error('Failed to approve withdrawal');
    return await response.json();
  } catch (error) {
    console.error('Error approving withdrawal:', error);
    throw error;
  }
};

export const rejectWithdrawal = async (id, remarks = '') => {
  try {
    const response = await apiFetch(`${API_URL}/transactions/admin/withdrawals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ remarks })
    });
    if (!response.ok) throw new Error('Failed to reject withdrawal');
    return await response.json();
  } catch (error) {
    console.error('Error rejecting withdrawal:', error);
    throw error;
  }
};

export const updateWithdrawalStatus = async (id, status, remarks = '') => {
  try {
    const response = await apiFetch(`${API_URL}/transactions/admin/withdrawals/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, remarks })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to update status');
    return data;
  } catch (error) {
    console.error('Error updating withdrawal status:', error);
    throw error;
  }
};

export const fetchHostWithdrawals = async (hostId) => {
  try {
    const response = await apiFetch(`${API_URL}/transactions/host/${hostId}/withdrawals`);
    if (!response.ok) throw new Error('Failed to fetch host withdrawals');
    return await response.json();
  } catch (error) {
    console.error('Error fetching host withdrawals:', error);
    throw error;
  }
};

export const submitWithdrawal = async (data) => {
  try {
    const response = await apiFetch(`${API_URL}/transactions/host/withdrawals/submit`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    const resData = await response.json();
    if (!response.ok) throw new Error(resData.message || 'Failed to submit withdrawal');
    return resData;
  } catch (error) {
    console.error('Error submitting withdrawal:', error);
    throw error;
  }
};

export const assignPropertyDeposit = async (propertyId, depositData) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/deposit/${propertyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(depositData)
    });
    const resData = await response.json();
    if (!response.ok) throw new Error(resData.message || 'Failed to assign deposit parameters');
    return resData;
  } catch (error) {
    console.error('Error assigning property deposit:', error);
    throw error;
  }
};

export const submitPropertyPayment = async (propertyId, paymentReference, receiptImageUrl = null) => {
  try {
    const response = await apiFetch(`${API_URL}/properties/payment/${propertyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentReference, receiptImageUrl })
    });
    const resData = await response.json();
    if (!response.ok) throw new Error(resData.message || 'Failed to submit property payment reference');
    return resData;
  } catch (error) {
    console.error('Error submitting property payment reference:', error);
    throw error;
  }
};

export const getSmsMode = async () => {
  try {
    const response = await apiFetch(`${API_URL}/admin/sms-mode`);
    if (!response.ok) throw new Error('Failed to fetch SMS mode');
    return await response.json();
  } catch (error) {
    console.error('Error fetching SMS mode:', error);
    throw error;
  }
};

export const toggleSmsMode = async (devMode) => {
  try {
    const response = await apiFetch(`${API_URL}/admin/sms-mode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ devMode })
    });
    if (!response.ok) throw new Error('Failed to toggle SMS mode');
    return await response.json();
  } catch (error) {
    console.error('Error toggling SMS mode:', error);
    throw error;
  }
};
